"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { api, ROLE_LABELS, User } from "@/lib/api";
import { useUser } from "@/components/auth";

function ThemeToggle() {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
  }, []);

  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  return (
    <button onClick={toggle} title={dark ? "라이트 모드로" : "다크 모드로"}
            className="mr-1 rounded border border-slate-600 px-2 py-1 text-sm hover:bg-slate-800">
      {dark ? "☀️" : "🌙"}
    </button>
  );
}

interface NavItem {
  href: string;
  label: string;
  roles: User["role"][];
}

// 5.1 역할별 화면 접근
const NAV_ITEMS: NavItem[] = [
  { href: "/dashboard", label: "대시보드", roles: ["operator", "admin"] },
  { href: "/members", label: "회원", roles: ["operator", "admin"] },
  { href: "/transactions", label: "원장", roles: ["operator", "admin"] },
  { href: "/input", label: "입력", roles: ["operator", "node_manager", "admin"] },
  { href: "/drafts", label: "검증입력", roles: ["operator", "node_manager", "admin"] },
  { href: "/approvals", label: "승인", roles: ["admin"] },
  { href: "/reconciliation", label: "대사센터", roles: ["operator", "admin"] },
  { href: "/loans", label: "차입금", roles: ["operator", "admin"] },
  { href: "/nodes", label: "노드", roles: ["operator", "node_manager", "admin"] },
  { href: "/closings", label: "마감", roles: ["operator", "admin"] },
  { href: "/users", label: "사용자", roles: ["admin"] },
];

export function Nav() {
  const user = useUser();
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  // 화면 이동 시 모바일 메뉴 닫기
  useEffect(() => setOpen(false), [pathname]);

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    router.replace("/login");
  };

  return (
    <header className="bg-slate-900 text-slate-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-1 px-4 py-2">
        <span className="mr-4 text-lg font-bold tracking-tight">CTA Ledger</span>
        {/* 데스크톱: 한 줄 메뉴 / 모바일: ☰ 버튼으로 여닫는 세로 메뉴 */}
        <nav
          className={`${open ? "flex" : "hidden"} order-last mt-2 w-full flex-col gap-1 border-t border-slate-600 pt-2 md:order-none md:mt-0 md:flex md:w-auto md:flex-1 md:flex-row md:flex-wrap md:border-0 md:pt-0`}
        >
          {NAV_ITEMS.filter((i) => i.roles.includes(user.role)).map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`rounded px-3 py-1.5 text-sm ${
                pathname.startsWith(item.href)
                  ? "bg-slate-700 font-semibold"
                  : "hover:bg-slate-800"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <span className="mr-2 ml-auto hidden text-sm text-slate-300 sm:inline md:ml-0">
          {user.display_name} · {ROLE_LABELS[user.role]}
        </span>
        <ThemeToggle />
        <button
          onClick={logout}
          className="rounded border border-slate-600 px-3 py-1 text-sm hover:bg-slate-800"
        >
          로그아웃
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="메뉴 열기"
          className="ml-1 rounded border border-slate-600 px-2.5 py-1 text-sm hover:bg-slate-800 md:hidden"
        >
          {open ? "✕" : "☰"}
        </button>
      </div>
    </header>
  );
}
